import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Stack,
  Chip,
  Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../../services/api';
import ApprovalsQueueTable from '../ApprovalsQueueTable';
import ReservationsStrip from '../ReservationsStrip';
import { useSidebarActions } from '../SidebarActionsContext';

const HOME_QUEUE_LIMIT = 5;

const HomePage = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const { vendorChangeNonce } = useSidebarActions();

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/vendors/pending');
      setPending(res.data);
    } catch (err) {
      console.error('Failed to load pending vendors:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending, vendorChangeNonce]);

  const totalPending = pending.length;
  const shown = pending.slice(0, HOME_QUEUE_LIMIT);

  return (
    <Box>
      <Tooltip title="Pick up where the festival needs attention." placement="right" arrow>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, mb: 1.5, display: 'inline-block', cursor: 'help' }}
        >
          Admin Dashboard
        </Typography>
      </Tooltip>

      <ReservationsStrip refreshNonce={vendorChangeNonce} />

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          // Refresh icon only fades in when the header is hovered, so it
          // doesn't fight for attention with the queue itself.
          '&:hover .approvals-refresh, & .approvals-refresh:focus-visible': {
            opacity: 1,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Pending Approvals
            </Typography>
            <Chip
              size="small"
              color={totalPending > 0 ? 'warning' : 'default'}
              label={totalPending}
            />
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={loadPending}
                aria-label="refresh pending approvals"
                className="approvals-refresh"
                sx={{ opacity: 0, transition: 'opacity 0.15s ease' }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View all approvals">
              <IconButton
                size="small"
                component={RouterLink}
                to="/admin/approvals"
                aria-label="view all approvals"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <ApprovalsQueueTable
          rows={shown}
          loading={loading}
          status="pending"
          onChange={loadPending}
          emptyText="No vendors waiting for approval right now."
        />

        {totalPending > HOME_QUEUE_LIMIT && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Showing {HOME_QUEUE_LIMIT} of {totalPending}.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default HomePage;
